// Utility to map numeric answers to string answers for walkAnswers
function mapApiAnswersToWalkAnswers(apiAnswers: any[]): WalkAnswer[] {
  return apiAnswers.map(ans => ({
    id: ans.id,
    answer:
      ans.answer === 1 ? 'yes' :
      ans.answer === 2 ? 'no' :
      ans.answer === 3 ? 'na' : undefined,
    actionPlan: ans.actionPlan,
    supervisor: ans.supervisor,
  }));
}
import { createSlice } from '@reduxjs/toolkit';

type WalkAnswer = {
  id: string;
  answer?: string;
  actionPlan?: string;
  supervisor?: string;
  section_value?: string;
  parent_section_value?: string;
  status?: string;
};

// Action Plan structure matching localStorage format
export interface SiteEvalActionPlan {
  id: string;
  localId: number;
  itemId: string;
  relatedId: string;
  siteEvaluationId?: number;
  questionId: string;
  title: string;
  actionPlan: string;
  supervisor: string;
  assignee: string;
  source: string;
  type: string | null;
  priority: string | null;
  startDate: string | null;
  targetDate: string | null;
  notes: string;
  frontlineInvolvement: boolean;
  employee: string | null;
  employeeName: string | null;
  createdAt: string;
  updatedAt: string;
  isReference: boolean;
  status: string;
  rawFormData?: any;
}

interface SiteEvalRedirectState {
  preWorkData: any[];
  preWorkAnswers: any[];
  preWorkColumns: any[];
  workData: any[];
  workColumns: any[];
  closeOutData: any[];
  closeOutColumns: any[];
  loadingWorkData: boolean;
  errorWorkData: string | null;
  walkAnswers: WalkAnswer[];
  closeOutAnswers: { [id: string]: { partner?: string } };
  preWorkTargets: { [id: string]: 'on-target' | 'off-target' | null };
  actionPlanSessionId: string | null;
  siteEvalActionPlans: SiteEvalActionPlan[];
}
import { mapApiWorkDataToAppFormat } from '../screens/siteEvaluation/SiteEvalRedirectScreen';
import { createApiClient, apiClient } from '../api/apiClient';
// Add centralized site-evaluation error utilities
import {
  safeSiteEvaluationApiCall,
  SITE_EVALUATION_API_NAMES,
} from './utils/siteEvaluationErrorHandler';




// Thunk to fetch and map workData using custom API client
// Utility to map close_out_questions to app format (similar to walk_questions)
export function mapApiCloseOutDataToAppFormat(apiData: any[]): any[] {
  return apiData.map((item) => {
    const mapped: any = {
      id: item.ID,
      ReviewFindings: item.Section_Value,
      parentId: item.Parent_Id === "NULL" ? undefined : item.Parent_Id,
      sortOrder: item.Sort_Order,
    };
    if (item.Section_Type === 'HEADER') {
      mapped.isHeader = true;
    } else if (item.Section_Type === 'SubHeader') {
      mapped.toggleOnly = true;
      mapped.isParent = true;
    } else if (item.Section_Type === 'DATA') {
      mapped.yesChecked = false;
      mapped.noChecked = false;
      mapped.naChecked = false;
    }
    return mapped;
  });
}

// Utility to map pre_work_questions to app format
export function mapApiPreWorkDataToAppFormat(apiData: any[]): any[] {
  return apiData.map((item) => ({
    id: item.ID || item.Data_Id,
    Section_Type: item.Section_Type, // Preserve Section_Type for filtering
    priorVisit: item.Prior_Visit !== undefined && item.Prior_Visit !== null && !isNaN(Number(item.Prior_Visit))
      ? Number(Number(item.Prior_Visit).toFixed(2))
      : 0,
    review: item.Section_Value || item.Review,
    lcp: item.LCP !== undefined && item.LCP !== null && !isNaN(Number(item.LCP))
      ? Number(Number(item.LCP).toFixed(2))
      : 0,
    ytd: item.YTD !== undefined && item.YTD !== null && !isNaN(Number(item.YTD))
      ? Number(Number(item.YTD).toFixed(2))
      : 0,
  }));
}

// Thunk to fetch and map all questions data
export const fetchAllQuestionsData = (location?: string) => async (dispatch: any, getState: any) => {
  try {
    dispatch(siteEvalRedirectSlice.actions.setLoadingWorkData(true));
    const api = createApiClient();
    const endpoint = location 
      ? `/site-evaluation-enhanced/questions/all?location=${encodeURIComponent(location)}`
      : '/site-evaluation-enhanced/questions/all';
    // Include org context params for new evaluations (this function is only called for new evaluations)
    const apiData = await apiClient(api, endpoint, 'GET', undefined);
    
    // Generate a new action_plan_session_id and store it in Redux only
    const actionPlanSessionId = `site_eval_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    dispatch(siteEvalRedirectSlice.actions.setActionPlanSessionId(actionPlanSessionId));
    console.log('[fetchAllQuestionsData] Generated new action_plan_session_id:', actionPlanSessionId);
    
    // Group flat array data by ID ranges - each section starts with a specific thousand
    const preWorkQuestions = apiData.filter((item: any) => 
      item.ID >= "1000" && item.ID < "2000"
    );

    // All work sections (Walk + all other sections 2000+) need to be together 
    // so the getSectionData function can find headers and their children
    const allWorkQuestions = apiData.filter((item: any) => 
      item.ID >= "2000"
    );

    // For close-out, we'll use a subset or empty array for now since the structure has changed
    const closeOutQuestions: any[] = [];

    // Map the grouped data using existing mapping functions
    dispatch(siteEvalRedirectSlice.actions.setWorkData(mapApiWorkDataToAppFormat(allWorkQuestions)));
    dispatch(siteEvalRedirectSlice.actions.setPreWorkData(mapApiPreWorkDataToAppFormat(preWorkQuestions)));
    dispatch(siteEvalRedirectSlice.actions.setCloseOutData(mapApiCloseOutDataToAppFormat(closeOutQuestions)));
    dispatch(siteEvalRedirectSlice.actions.setLoadingWorkData(false));
  } catch (error) {
    dispatch(siteEvalRedirectSlice.actions.setLoadingWorkData(false));
    const errorMessage =
      error && typeof error === 'object' && 'message' in error
        ? (error as { message: string }).message
        : 'Failed to fetch questions data';
    dispatch(siteEvalRedirectSlice.actions.setErrorWorkData(errorMessage));
  }
};

const initialState: SiteEvalRedirectState = {
  preWorkData: [],
  preWorkColumns: [],
  workData: [],
  workColumns: [],
  closeOutData: [],
  closeOutColumns: [],
  loadingWorkData: false,
  errorWorkData: null,
  preWorkAnswers: [],
  walkAnswers: [],
  closeOutAnswers: {},
  preWorkTargets: {},
  actionPlanSessionId: null,
  siteEvalActionPlans: [],
};


const siteEvalRedirectSlice = createSlice({
  name: 'siteEvalRedirect',
  initialState,
  reducers: {
    setWorkData: (state, action) => {
      state.workData = action.payload;
    },
    setPreWorkData: (state, action) => {
      state.preWorkData = action.payload;
    },
    setPreWorkAnswers: (state, action) => {
      state.preWorkAnswers = action.payload;
    },
    setCloseOutData: (state, action) => {
      state.closeOutData = action.payload;
    },
    setLoadingWorkData: (state, action) => {
      state.loadingWorkData = action.payload;
    },
    setErrorWorkData: (state, action) => {
      state.errorWorkData = action.payload;
    },
    resetSiteEvalRedirect: (state) => {
      state.workData = [];
      state.closeOutData = [];
      state.preWorkData = [];
      state.loadingWorkData = false;
      state.errorWorkData = null;
      state.walkAnswers = [];
      state.closeOutAnswers = {};
      state.preWorkAnswers = [];
      state.preWorkTargets = {};
      state.actionPlanSessionId = null;
      state.siteEvalActionPlans = [];
    },
    setPreWorkTarget: (state, action) => {
      const { id, target } = action.payload as { id: string; target: 'on-target' | 'off-target' | null };
      if (target === null) {
        delete state.preWorkTargets[id];
      } else {
        state.preWorkTargets[id] = target;
      }
    },
    removePreWorkTarget: (state, action) => {
      const id = action.payload as string;
      delete state.preWorkTargets[id];
    },
    setWalkAnswer: (state, action) => {
      const { id, answer } = action.payload as { id: string, answer: string };
      const idx = state.walkAnswers.findIndex(ans => ans.id === id);
      if (idx === -1) {
        state.walkAnswers.push({ id, answer });
      } else {
        state.walkAnswers[idx].answer = answer;
      }
    },
    setWalkOverlayData: (state, action) => {
      console.log('Setting walk overlay data:', action.payload);
      const { id, actionPlan, supervisor, section_value, parent_section_value, status } = action.payload as {
        id: string;
        actionPlan: string;
        supervisor: string;
        section_value: string;
        parent_section_value: string;
        status: string;
      };
      const idx = state.walkAnswers.findIndex(ans => ans.id === id);
      if (idx === -1) {
        state.walkAnswers.push({ id, actionPlan, supervisor, section_value, parent_section_value, status });
      } else {
        state.walkAnswers[idx].actionPlan = actionPlan;
        state.walkAnswers[idx].supervisor = supervisor;
      }
    },
    removeWalkAnswer: (state, action) => {
      const id = action.payload as string;
      state.walkAnswers = state.walkAnswers.filter(ans => ans.id !== id);
    },
    setCloseOutAnswer: (state, action) => {
      const { id, partner } = action.payload as { id: string, partner: string };
      state.closeOutAnswers[id] = { partner };
    },
    removeCloseOutAnswer: (state, action) => {
      const id = action.payload as string;
      delete state.closeOutAnswers[id];
    },
    setActionPlanSessionId: (state, action) => {
      state.actionPlanSessionId = action.payload as string;
    },
    // Action Plan Management Reducers
    addSiteEvalActionPlan: (state, action) => {
      const newPlan = action.payload as SiteEvalActionPlan;
      state.siteEvalActionPlans.push(newPlan);
      console.log('[Redux] Added action plan:', newPlan.itemId);
    },
    updateSiteEvalActionPlan: (state, action) => {
      const updatedPlan = action.payload as SiteEvalActionPlan;
      const index = state.siteEvalActionPlans.findIndex(
        plan => plan.itemId === updatedPlan.itemId || plan.relatedId === updatedPlan.relatedId
      );
      if (index !== -1) {
        state.siteEvalActionPlans[index] = { ...state.siteEvalActionPlans[index], ...updatedPlan };
        console.log('[Redux] Updated action plan:', updatedPlan.itemId);
      } else {
        state.siteEvalActionPlans.push(updatedPlan);
        console.log('[Redux] Added new action plan (not found for update):', updatedPlan.itemId);
      }
    },
    removeSiteEvalActionPlan: (state, action) => {
      const itemId = action.payload as string;
      state.siteEvalActionPlans = state.siteEvalActionPlans.filter(
        plan => plan.itemId !== itemId && plan.relatedId !== itemId
      );
      console.log('[Redux] Removed action plan:', itemId);
    },
    clearSiteEvalActionPlans: (state) => {
      state.siteEvalActionPlans = [];
      console.log('[Redux] Cleared all action plans');
    },
    setSiteEvalActionPlans: (state, action) => {
      state.siteEvalActionPlans = action.payload as SiteEvalActionPlan[];
      console.log('[Redux] Set action plans:', action.payload.length);
    },
  },
});

export const { 
  setWorkData, 
  setLoadingWorkData, 
  setErrorWorkData, 
  setWalkAnswer, 
  setWalkOverlayData, 
  removeWalkAnswer, 
  setCloseOutAnswer, 
  removeCloseOutAnswer, 
  resetSiteEvalRedirect, 
  setPreWorkData, 
  setPreWorkAnswers, 
  setPreWorkTarget, 
  removePreWorkTarget, 
  setActionPlanSessionId,
  addSiteEvalActionPlan,
  updateSiteEvalActionPlan,
  removeSiteEvalActionPlan,
  clearSiteEvalActionPlans,
  setSiteEvalActionPlans,
  setCloseOutData,
} = siteEvalRedirectSlice.actions;

export const selectWalkAnswers = (state: any) => state.siteEvalRedirect.walkAnswers;
export const selectCloseOutAnswers = (state: any) => state.siteEvalRedirect.closeOutAnswers;
export default siteEvalRedirectSlice.reducer;
export const selectPreWorkAnswers = (state: any) => state.siteEvalRedirect.preWorkAnswers;
export const selectPreWorkData = (state: any) => state.siteEvalRedirect.preWorkData;
export const selectPreWorkColumns = (state: any) => state.siteEvalRedirect.preWorkColumns;
export const selectWorkData = (state: any) => state.siteEvalRedirect.workData;
export const selectWorkColumns = (state: any) => state.siteEvalRedirect.workColumns;
export const selectCloseOutData = (state: any) => state.siteEvalRedirect.closeOutData;
export const selectCloseOutColumns = (state: any) => state.siteEvalRedirect.closeOutColumns;
export const selectPreWorkTargets = (state: any) => state.siteEvalRedirect.preWorkTargets;
export const selectActionPlanSessionId = (state: any) => state.siteEvalRedirect.actionPlanSessionId;
export const selectSiteEvalActionPlans = (state: any) => state.siteEvalRedirect.siteEvalActionPlans;
